/**
 * useIdentityFieldPolicy — fetches the framework-driven identity field policy
 * for the active organization.
 *
 * Drives:
 *   - Users-tab clearance dropdown filter (hide entirely if not visible)
 *   - Create User wizard Clearance step (skip if no fields visible)
 *   - User detail Security panel (hide invisible fields, label preserved data
 *     as "preserved from prior framework activation" if value is non-default)
 *
 * Backend: GET /api/v1/users/identity-fields/policy. Response shape mirrors
 * resolveIdentityFieldPolicy() in @argusai/shared.
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from './api-client';
import {
  IdentityFieldKeySchema,
  type ComplianceFramework,
  type IdentityFieldKey,
} from '@argusai/shared/schemas/identity-field-policy';

export interface IdentityFieldPolicyResponse {
  activeFrameworks: ComplianceFramework[];
  visible: IdentityFieldKey[];
  required: IdentityFieldKey[];
  forbidden: IdentityFieldKey[];
}

/**
 * Lightweight policy view materialized as Set<>s for O(1) lookups in render
 * paths.
 */
export interface IdentityFieldPolicyView {
  activeFrameworks: ComplianceFramework[];
  visible: Set<IdentityFieldKey>;
  required: Set<IdentityFieldKey>;
  forbidden: Set<IdentityFieldKey>;
  hasAny: boolean;
  isVisible: (k: IdentityFieldKey) => boolean;
  isRequired: (k: IdentityFieldKey) => boolean;
}

const EMPTY_VIEW: IdentityFieldPolicyView = {
  activeFrameworks: [],
  visible: new Set(),
  required: new Set(),
  // USR-F1-T1 (principle 3) — every identity key, from THE declaration,
  // never a hand-typed copy of it.
  forbidden: new Set<IdentityFieldKey>(IdentityFieldKeySchema.options),
  hasAny: false,
  isVisible: () => false,
  isRequired: () => false,
};

export function useIdentityFieldPolicy(): {
  policy: IdentityFieldPolicyView;
  isLoading: boolean;
  error: unknown;
} {
  const { data, isLoading, error } = useQuery<IdentityFieldPolicyResponse>({
    queryKey: ['identity-field-policy'],
    queryFn: () =>
      apiClient.get<IdentityFieldPolicyResponse>('/users/identity-fields/policy'),
    staleTime: 60_000,
  });

  if (!data) return { policy: EMPTY_VIEW, isLoading, error };

  const visible = new Set(data.visible);
  const required = new Set(data.required);
  const forbidden = new Set(data.forbidden);

  const policy: IdentityFieldPolicyView = {
    activeFrameworks: data.activeFrameworks,
    visible,
    required,
    forbidden,
    hasAny: visible.size > 0,
    isVisible: (k) => visible.has(k),
    isRequired: (k) => required.has(k),
  };

  return { policy, isLoading, error };
}
